import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/store/authStore'
import { isAllowed, navItems, type PermissionName } from '@/router/navigation'

/** Routes for every page in the navigation tree, so the two cannot drift apart. */
const navRoutes: RouteRecordRaw[] = navItems.map((item) => ({
  path: item.path,
  name: item.name,
  component: item.component,
  meta: { permissions: item.permissions }
}))

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      { path: '', redirect: { name: 'dashboard' } },
      ...navRoutes,
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/ForbiddenView.vue')
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true }
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 })
})

/**
 * Everything is private unless a route says otherwise, so adding a page cannot accidentally make it
 * public. This mirrors the backend's `PUBLIC_PATHS` allowlist.
 *
 * Role checks here are a usability measure, not a security boundary: the backend re-checks every
 * request, because anything decided in a browser can be edited in a browser.
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // On a hard reload the access token is gone (it lives in memory only), so try to re-establish the
  // session before deciding whether the user is signed in.
  if (!auth.restored) {
    await auth.restore()
  }

  if (to.meta.public) {
    // Someone already signed in has no business on the login screen.
    if (to.name === 'login' && auth.isSignedIn) {
      return { name: 'dashboard' }
    }
    return true
  }

  if (!auth.isSignedIn) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }

  const required = to.meta.permissions as PermissionName[] | undefined
  if (!isAllowed(auth.permissions, required)) {
    return { name: 'forbidden', query: { from: to.fullPath } }
  }

  return true
})
